import React, { useState } from 'react';
import {
  Box, Typography, Button, Paper, CircularProgress, Chip, Table, TableHead,
  TableRow, TableCell, TableBody, Tooltip, Alert, Collapse, IconButton,
} from '@mui/material';
import SearchIcon        from '@mui/icons-material/Search';
import CheckCircleIcon   from '@mui/icons-material/CheckCircle';
import WarningAmberIcon  from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon  from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon    from '@mui/icons-material/ExpandMore';
import ExpandLessIcon    from '@mui/icons-material/ExpandLess';
import StorageIcon       from '@mui/icons-material/Storage';
import WebIcon           from '@mui/icons-material/Web';
import CodeIcon          from '@mui/icons-material/Code';
import api from '../utils/api';

const fmtTime = iso => iso ? new Date(iso).toLocaleString('en-US', { month:'short', day:'numeric', hour:'2-digit', minute:'2-digit' }) : '';

function DiffBadge({ differs }) {
  return differs
    ? <Chip label="DIFF" size="small" icon={<WarningAmberIcon sx={{ fontSize:'13px !important' }} />}
        sx={{ height:20, fontSize:'0.65rem', fontWeight:700, backgroundColor:'#fef3c7', color:'#92400e', borderRadius:'5px', ml:0.5 }} />
    : <Chip label="MATCH" size="small" icon={<CheckCircleIcon sx={{ fontSize:'13px !important' }} />}
        sx={{ height:20, fontSize:'0.65rem', fontWeight:700, backgroundColor:'#dcfce7', color:'#166534', borderRadius:'5px', ml:0.5 }} />;
}

function SummaryCard({ label, values, differs }) {
  return (
    <Paper elevation={0} sx={{ borderRadius:'12px', border: differs ? '1.5px solid #f59e0b' : '1px solid rgba(47,99,173,0.08)', p:2, flex:1, minWidth:180 }}>
      <Typography variant="caption" sx={{ color:'#6b778c', fontWeight:700, fontSize:'0.7rem', textTransform:'uppercase' }}>{label}</Typography>
      <Box sx={{ mt:1, display:'flex', flexDirection:'column', gap:0.5 }}>
        {Object.entries(values).map(([tenant, val]) => (
          <Box key={tenant} sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <Typography variant="caption" sx={{ color:'#6b778c' }}>{tenant}</Typography>
            <Typography variant="caption" sx={{ fontFamily:'monospace', fontWeight:700, color: differs ? '#92400e' : '#1e3a5f' }}>{val || '-'}</Typography>
          </Box>
        ))}
      </Box>
      {differs && <Alert severity="warning" sx={{ mt:1.5, py:0, px:1, borderRadius:'8px', fontSize:'0.72rem' }}>Values differ between tenants</Alert>}
    </Paper>
  );
}

function FileTable({ title, icon, files, tenants, diffs, diffPrefix, open, onToggle }) {
  const fileDiffs = files.filter(f => !!diffs[`${diffPrefix}${f}`]).length;
  return (
    <Paper elevation={0} sx={{ borderRadius:'16px', border:'1px solid rgba(47,99,173,0.08)', overflow:'hidden' }}>
      <Box sx={{ px:3, py:2, borderBottom:'1px solid rgba(47,99,173,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          {icon}
          <Typography sx={{ fontWeight:700, color:'#1e3a5f' }}>{title}</Typography>
          <DiffBadge differs={fileDiffs > 0} />
          {fileDiffs > 0 && <Typography variant="caption" sx={{ color:'#92400e', fontWeight:600 }}>{fileDiffs} file{fileDiffs>1?'s':''} differ</Typography>}
        </Box>
        <IconButton size="small" onClick={onToggle}>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>
      <Collapse in={open}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor:'#f8fafc' }}>
              <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>File</TableCell>
              {tenants.map(t => (
                <TableCell key={t.id} sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>{t.slug}</TableCell>
              ))}
              <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map(f => {
              const differs = !!diffs[`${diffPrefix}${f}`];
              return (
                <TableRow key={f} sx={{ backgroundColor: differs ? '#fffbeb' : 'inherit' }}>
                  <TableCell sx={{ fontFamily:'monospace', fontSize:'0.75rem', color:'#445', py:0.8 }}>{f.replace('my-react-app/', '')}</TableCell>
                  {tenants.map(t => {
                    const md5 = t.checksums?.[f] || null;
                    return (
                      <TableCell key={t.id} sx={{ fontFamily:'monospace', fontSize:'0.72rem', color: differs ? '#92400e' : '#6b778c', py:0.8 }}>
                        <Tooltip title={md5 || 'N/A'}>
                          <span>{md5 ? md5.slice(0,8)+'…' : '-'}</span>
                        </Tooltip>
                      </TableCell>
                    );
                  })}
                  <TableCell sx={{ py:0.8 }}><DiffBadge differs={differs} /></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Collapse>
    </Paper>
  );
}

// Normalize table name for grouping: lowercase, strip underscores
const norm = name => name.toLowerCase().replace(/_/g, '');

function buildTableGroups(tenants) {
  // Map: normalizedKey -> { key, perTenant: { [slug]: {actualName, cols} } }
  const groups = {};
  tenants.filter(t => Array.isArray(t.dbTables)).forEach(t => {
    t.dbTables.forEach(({ n, c }) => {
      const key = norm(n);
      if (!groups[key]) groups[key] = { key, perTenant: {} };
      // If tenant already has an entry for this normalized key, keep the one that matches PascalCase (shorter, no underscore)
      const existing = groups[key].perTenant[t.slug];
      if (!existing || n.replace(/_/g,'').length === n.length) {
        groups[key].perTenant[t.slug] = { actualName: n, cols: c };
      }
    });
  });
  return Object.values(groups).sort((a, b) => a.key.localeCompare(b.key));
}

function getGroupStatus(group, tenants) {
  const entries = tenants.map(t => group.perTenant[t.slug]);
  const present = entries.filter(Boolean);
  if (present.length < tenants.filter(t => Array.isArray(t.dbTables)).length) return 'missing';
  const names = [...new Set(present.map(e => e.actualName))];
  const cols  = [...new Set(present.map(e => e.cols))];
  if (cols.length > 1) return 'diff';
  if (names.length > 1) return 'renamed';
  return 'match';
}

function DbTablesSection({ tenants, diffs, open, onToggle }) {
  const [filter, setFilter] = useState('all');
  const hasError = tenants.some(t => t.dbTables?.error);
  const groups = buildTableGroups(tenants);

  const statusOf = g => getGroupStatus(g, tenants);
  const filtered = filter === 'all' ? groups : groups.filter(g => {
    const s = statusOf(g);
    if (filter === 'issues') return s !== 'match';
    return s === filter;
  });

  const counts = { diff: 0, missing: 0, renamed: 0, match: 0 };
  groups.forEach(g => { const s = statusOf(g); if (counts[s] !== undefined) counts[s]++; });
  const issueCount = counts.diff + counts.missing + counts.renamed;

  const statusChip = (status) => {
    const map = {
      match:   { label:'MATCH',   bg:'#dcfce7', color:'#166534', icon:<CheckCircleIcon sx={{ fontSize:'13px !important' }} /> },
      diff:    { label:'COL DIFF', bg:'#fef3c7', color:'#92400e', icon:<WarningAmberIcon sx={{ fontSize:'13px !important' }} /> },
      missing: { label:'MISSING',  bg:'#fee2e2', color:'#991b1b', icon:<ErrorOutlineIcon sx={{ fontSize:'13px !important' }} /> },
      renamed: { label:'RENAMED',  bg:'#ede9fe', color:'#5b21b6', icon:<WarningAmberIcon sx={{ fontSize:'13px !important' }} /> },
    };
    const s = map[status] || map.match;
    return <Chip label={s.label} size="small" icon={s.icon}
      sx={{ height:20, fontSize:'0.65rem', fontWeight:700, backgroundColor:s.bg, color:s.color, borderRadius:'5px' }} />;
  };

  return (
    <Paper elevation={0} sx={{ borderRadius:'16px', border:'1px solid rgba(47,99,173,0.08)', overflow:'hidden' }}>
      <Box sx={{ px:3, py:2, borderBottom:'1px solid rgba(47,99,173,0.06)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <Box sx={{ display:'flex', alignItems:'center', gap:1, flexWrap:'wrap' }}>
          <StorageIcon sx={{ color:'#6b778c', fontSize:20 }} />
          <Typography sx={{ fontWeight:700, color:'#1e3a5f' }}>Database Tables</Typography>
          <Chip label={`${groups.length} tables`} size="small" sx={{ height:20, fontSize:'0.68rem', backgroundColor:'#e2e8f0', color:'#475569', borderRadius:'5px' }} />
          {counts.diff > 0    && <Chip label={`${counts.diff} col diff`}  size="small" sx={{ height:20, fontSize:'0.68rem', backgroundColor:'#fef3c7', color:'#92400e', borderRadius:'5px' }} />}
          {counts.missing > 0 && <Chip label={`${counts.missing} missing`} size="small" sx={{ height:20, fontSize:'0.68rem', backgroundColor:'#fee2e2', color:'#991b1b', borderRadius:'5px' }} />}
          {counts.renamed > 0 && <Chip label={`${counts.renamed} renamed`} size="small" sx={{ height:20, fontSize:'0.68rem', backgroundColor:'#ede9fe', color:'#5b21b6', borderRadius:'5px' }} />}
        </Box>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          {['all','issues','diff','missing','renamed','match'].map(f => (
            <Chip key={f} label={f} size="small" onClick={() => setFilter(f)}
              sx={{ height:22, fontSize:'0.68rem', cursor:'pointer', fontWeight: filter===f ? 700 : 400,
                backgroundColor: filter===f ? '#2f63ad' : '#f1f5f9', color: filter===f ? '#fff' : '#475569', borderRadius:'6px' }} />
          ))}
          <IconButton size="small" onClick={onToggle}>
            {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      <Collapse in={open}>
        {tenants.map(t => t.dbTables?.error && (
          <Alert key={t.id} severity="warning" sx={{ m:2, borderRadius:'10px', fontSize:'0.8rem' }}>
            <strong>{t.name}</strong>: DB connection failed - {t.dbTables.error}
          </Alert>
        ))}
        {groups.length === 0 && !hasError && (
          <Box sx={{ p:3, textAlign:'center' }}>
            <Typography variant="body2" sx={{ color:'#9aa5b4' }}>No tables found - DB may be unreachable on all tenants</Typography>
          </Box>
        )}
        {filtered.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor:'#f8fafc' }}>
                <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Table (normalized)</TableCell>
                {tenants.map(t => (
                  <TableCell key={t.id} sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>
                    {t.slug}
                    <Typography variant="caption" sx={{ display:'block', color:'#9aa5b4' }}>name / cols</Typography>
                  </TableCell>
                ))}
                <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map(g => {
                const status = statusOf(g);
                const rowBg = { diff:'#fffbeb', missing:'#fff1f2', renamed:'#f5f3ff', match:'inherit' }[status];
                return (
                  <TableRow key={g.key} sx={{ backgroundColor: rowBg }}>
                    <TableCell sx={{ fontFamily:'monospace', fontSize:'0.72rem', color:'#64748b', py:0.8 }}>{g.key}</TableCell>
                    {tenants.map(t => {
                      if (t.dbTables?.error) return <TableCell key={t.id} sx={{ color:'#dc2626', fontSize:'0.72rem', py:0.8 }}>err</TableCell>;
                      if (!Array.isArray(t.dbTables)) return <TableCell key={t.id} sx={{ color:'#9aa5b4', py:0.8 }}>-</TableCell>;
                      const entry = g.perTenant[t.slug];
                      if (!entry) return <TableCell key={t.id} sx={{ py:0.8 }}><span style={{ color:'#dc2626', fontWeight:700, fontSize:'0.72rem' }}>MISSING</span></TableCell>;
                      return (
                        <TableCell key={t.id} sx={{ py:0.8 }}>
                          <Typography sx={{ fontFamily:'monospace', fontSize:'0.72rem', color:'#1e3a5f', fontWeight:600 }}>{entry.actualName}</Typography>
                          <Typography sx={{ fontFamily:'monospace', fontSize:'0.7rem', color: status==='diff' ? '#92400e' : '#6b778c' }}>{entry.cols} cols</Typography>
                        </TableCell>
                      );
                    })}
                    <TableCell sx={{ py:0.8 }}>{statusChip(status)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Collapse>
    </Paper>
  );
}

export default function Scan() {
  const [loading, setLoading]         = useState(false);
  const [result, setResult]           = useState(null);
  const [showBackend, setShowBackend] = useState(false);
  const [showFrontend, setShowFrontend] = useState(false);
  const [showDB, setShowDB]           = useState(true);

  const runScan = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await api.post('/tenants/scan-all');
      setResult(r.data);
    } catch (err) {
      setResult({ error: err.response?.data?.message || 'Scan failed' });
    } finally { setLoading(false); }
  };

  const tenants     = result?.tenants || [];
  const diffs       = result?.diffs || {};
  const goodTenants = tenants.filter(t => !t.error);
  const backendFiles  = result?.backendFiles || [];
  const frontendFiles = result?.frontendFiles || [];

  const hasDiffs = Object.keys(diffs).length > 0;

  return (
    <Box>
      <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center', mb:3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight:800, color:'#1e3a5f' }}>Tenant Scan</Typography>
          <Typography variant="body2" sx={{ color:'#6b778c' }}>Read-only audit - versions, migrations, DB schema, backend &amp; frontend files across all tenants</Typography>
        </Box>
        <Button variant="contained" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SearchIcon />}
          onClick={runScan} disabled={loading}
          sx={{ borderRadius:'10px', textTransform:'none', fontWeight:700, backgroundColor:'#2f63ad', boxShadow:'none', '&:hover':{ backgroundColor:'#1f4f94', boxShadow:'none' } }}>
          {loading ? 'Scanning…' : 'Run Scan'}
        </Button>
      </Box>

      {loading && (
        <Paper elevation={0} sx={{ borderRadius:'16px', border:'1px solid rgba(47,99,173,0.08)', p:6, textAlign:'center' }}>
          <CircularProgress sx={{ color:'#2f63ad', mb:2 }} />
          <Typography variant="body2" sx={{ color:'#6b778c' }}>SSHing into each tenant - collecting versions, DB schema and file checksums… this may take 30–45 seconds.</Typography>
        </Paper>
      )}

      {result?.error && <Alert severity="error" sx={{ borderRadius:'12px' }}>{result.error}</Alert>}

      {result && !result.error && (
        <Box sx={{ display:'flex', flexDirection:'column', gap:3 }}>

          {/* Overall summary banner */}
          <Alert severity={hasDiffs ? 'warning' : 'success'} icon={hasDiffs ? <WarningAmberIcon /> : <CheckCircleIcon />}
            sx={{ borderRadius:'12px', fontWeight:600 }}>
            {hasDiffs
              ? `${Object.keys(diffs).length} difference(s) found across ${goodTenants.length} tenant(s) - reviewed at ${fmtTime(result.scannedAt)}`
              : `All ${goodTenants.length} tenant(s) are in sync - reviewed at ${fmtTime(result.scannedAt)}`}
          </Alert>

          {/* Version & environment cards */}
          <Box sx={{ display:'flex', gap:2, flexWrap:'wrap' }}>
            <SummaryCard label="App Version" differs={!!diffs.version}
              values={Object.fromEntries(tenants.map(t => [t.slug, t.error ? 'SSH error' : t.version]))} />
            <SummaryCard label="Node.js" differs={!!diffs.node}
              values={Object.fromEntries(tenants.map(t => [t.slug, t.error ? 'SSH error' : t.node]))} />
            <SummaryCard label="Restarts" differs={false}
              values={Object.fromEntries(tenants.map(t => [t.slug, t.error ? '-' : t.restarts]))} />
            <SummaryCard label="Uptime" differs={false}
              values={Object.fromEntries(tenants.map(t => [t.slug, t.error ? '-' : t.uptime]))} />
          </Box>

          {/* Migrations table */}
          <Paper elevation={0} sx={{ borderRadius:'16px', border:'1px solid rgba(47,99,173,0.08)', overflow:'hidden' }}>
            <Box sx={{ px:3, py:2, borderBottom:'1px solid rgba(47,99,173,0.06)', display:'flex', alignItems:'center', gap:1 }}>
              <Typography sx={{ fontWeight:700, color:'#1e3a5f' }}>Migrations</Typography>
            </Box>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor:'#f8fafc' }}>
                  <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Tenant</TableCell>
                  <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Applied</TableCell>
                  <TableCell sx={{ fontWeight:700, fontSize:'0.78rem', color:'#5f6b7a' }}>Pending</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tenants.map(t => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ fontWeight:700, color:'#1e3a5f', py:1 }}>
                      {t.name}
                      <Typography variant="caption" sx={{ display:'block', color:'#9aa5b4' }}>/{t.slug}</Typography>
                    </TableCell>
                    <TableCell sx={{ py:1 }}>
                      {t.error
                        ? <Chip label="SSH Error" size="small" sx={{ height:20, fontSize:'0.68rem', backgroundColor:'#fee2e2', color:'#dc2626', borderRadius:'5px' }} />
                        : <Chip label={`${t.migrations.applied} / ${t.migrations.total}`} size="small"
                            sx={{ height:20, fontSize:'0.72rem', fontWeight:700, borderRadius:'5px',
                              backgroundColor: t.migrations.pending.length ? '#fef3c7' : '#dcfce7',
                              color: t.migrations.pending.length ? '#92400e' : '#166534' }} />
                      }
                    </TableCell>
                    <TableCell sx={{ py:1 }}>
                      {t.error ? '-' : t.migrations.pending.length === 0
                        ? <Typography variant="caption" sx={{ color:'#16a34a', fontWeight:600 }}>Up to date</Typography>
                        : <Box sx={{ display:'flex', flexWrap:'wrap', gap:0.5 }}>
                            {t.migrations.pending.map(f => (
                              <Chip key={f} label={f} size="small"
                                sx={{ height:20, fontSize:'0.65rem', fontFamily:'monospace', backgroundColor:'#fff7ed', color:'#c2410c', borderRadius:'5px' }} />
                            ))}
                          </Box>
                      }
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>

          {/* DB Tables */}
          <DbTablesSection tenants={goodTenants} diffs={diffs} open={showDB} onToggle={() => setShowDB(v => !v)} />

          {/* Backend file checksums */}
          {backendFiles.length > 0 && (
            <FileTable
              title="Backend Files" icon={<CodeIcon sx={{ color:'#6b778c', fontSize:20 }} />}
              files={backendFiles} tenants={tenants} diffs={diffs} diffPrefix="file:"
              open={showBackend} onToggle={() => setShowBackend(v => !v)}
            />
          )}

          {/* Frontend file checksums */}
          {frontendFiles.length > 0 && (
            <FileTable
              title="Frontend Files" icon={<WebIcon sx={{ color:'#6b778c', fontSize:20 }} />}
              files={frontendFiles} tenants={tenants} diffs={diffs} diffPrefix="fe:"
              open={showFrontend} onToggle={() => setShowFrontend(v => !v)}
            />
          )}

          {/* SSH errors */}
          {tenants.filter(t => t.error).map(t => (
            <Alert key={t.id} severity="error" icon={<ErrorOutlineIcon />} sx={{ borderRadius:'12px' }}>
              <strong>{t.name}</strong> ({t.ssh_host}) - {t.error}
            </Alert>
          ))}
        </Box>
      )}
    </Box>
  );
}
